import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PetType } from '@spike/model';
import { Room } from '@spike/room-model';
import { fetchOrganizationServicesInfoThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { OverFullWindow, Spinner } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import { useMasterData, useMarketplace, useApiClientWrapper } from 'hooks';
import {
    createEmptyBoardingService,
    Service,
    ServicePricing,
    FixedPriceOption,
    LateCheckoutFeeType,
    MetadataDuration,
    BoardingMetadata,
    BillingCalculation,
    buildBoardingRawMetadata,
    dropoff24hFromBillingCalculation,
    resolveBillingCalculation,
    LATE_CHECKOUT_FEE_TYPES
} from 'model/Service';
import { integerTimeToString, timeStringToInteger } from 'utils/TimeOptionsUtils';
import { saveBulkServicesThunk, fetchServicesThunk } from 'actions/services/ServicesActions';
import { RootState } from 'store';
import styles from './BoardingForm.module.scss';
import { BoardingPetTypeStep } from './Steps/PetType/BoardingPetTypeStep';
import { BoardingDetailsStep } from './Steps/Details/BoardingDetailsStep';
import { BoardingPricingStep } from './Steps/Pricing/BoardingPricingStep';

interface TimeRange {
    from: string;
    to: string;
}
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import useNonInitialEffect from '@versiondos/hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';

// Props for both add and edit modes
interface BoardingFormProps {
    mode: 'add' | 'edit';
    service?: Service; // Required for edit mode
    rooms: Room[];
    roomsLoading: boolean;
    onClose: () => void;
}

// Local interface for rooms (no longer needed since we receive rooms as props)

export const BoardingForm: React.FC<BoardingFormProps> = props => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const fetchPendingRef = useRef(false);

    // Use global RootState for services
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    // Use rooms from props instead of fetching
    const { rooms, roomsLoading } = props;
    const [formStep, setFormStep] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOrganizationServices, setIsLoadingOrganizationServices] = useState(true);
    const [isEditingSpecificStep, setIsEditingSpecificStep] = useState(false);

    // Conditional based on mode
    const [boardingService, setBoardingService] = useState<Service>(() => {
        if (props.mode === 'edit' && props.service) {
            return props.service;
        }
        return createEmptyBoardingService(masterData, marketplace);
    });

    const [showExitPrompt, setShowExitPrompt] = useState(false);

    // Load organization services info and related services data on mount
    useEffect(() => {
        setIsLoadingOrganizationServices(true);
        fetchPendingRef.current = false;
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        dispatch(
            fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'grooming,dental')
        );
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'daycare'));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchingOrganizationServicesInfo) {
            fetchPendingRef.current = true;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.FetchOrganizationServicesInfoSuccess) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.Error) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }
    }, [marketplaceStatus]);

    // Handle services status changes
    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.SaveSuccess:
            case ServicesStatus.SaveBulkSuccess:
                setIsLoading(false);
                setIsSuccess(true);
                break;
            case ServicesStatus.Error:
                setIsLoading(false);
                setIsSuccess(false);
                break;
        }
    }, [servicesStatus]);

    // Get available rooms from props (already filtered to boarding-enabled rooms)
    const availableRooms = useMemo(() => {
        return rooms || [];
    }, [rooms]);

    // Get available rooms filtered by selected pet types
    const availableRoomsFiltered = useMemo(() => {
        if (!availableRooms.length || !boardingService.petTypes?.length) return availableRooms;

        const selectedPetTypeIds = boardingService.petTypes.map(pt => pt.id);

        // Filter rooms that support ALL of the selected pet types
        const compatibleRooms = availableRooms.filter(room => {
            if (!room.petTypes?.length) return false;
            const roomPetTypeIds = room.petTypes.map(pt => pt.id);
            return selectedPetTypeIds.every(selectedId => roomPetTypeIds.includes(selectedId));
        });

        return compatibleRooms;
    }, [availableRooms, boardingService.petTypes]);

    //Auto sync pricing when pet types change
    useEffect(() => {
        if (!boardingService.petTypes || !boardingService.pricing?.fixedPrice) return;

        const petTypeIds = boardingService.petTypes.map(pt => pt.id);
        const currentPricing = boardingService.pricing.fixedPrice;

        // Find pet types that need pricing
        const missingPetTypes = boardingService.petTypes.filter(
            pt => !currentPricing.some(fp => fp.petType.id === pt.id)
        );

        // Remove pricing of pet types that are no longer selected
        const validPricing = currentPricing.filter(fp => petTypeIds.includes(fp.petType.id));

        // If there are changes, auto-sync
        if (missingPetTypes.length > 0 || validPricing.length !== currentPricing.length) {
            const newPricing: FixedPriceOption[] = [
                ...validPricing,
                ...missingPetTypes.map(petType => ({
                    petType,
                    price: 0
                }))
            ];

            setBoardingService(prev => ({
                ...prev,
                pricing: {
                    ...prev.pricing!,
                    fixedPrice: newPricing
                }
            }));
        }
    }, [boardingService.petTypes]);

    // Adaptive strings based on mode
    const getTitle = () => {
        return props.mode === 'edit' ? `Editing ${boardingService.name}` : 'Adding a Boarding Service';
    };

    const getSuccessTitle = () => {
        return props.mode === 'edit' ? 'Updated Successfully' : 'Good Job';
    };

    const getSuccessMessage = () => {
        const action = props.mode === 'edit' ? 'updated' : 'added';
        return (
            <p className={styles.successDescription}>
                <b>{boardingService.name}</b> was {action} successfully.
            </p>
        );
    };

    const getSuccessButtonLabel = () => {
        return 'Go to Boarding Services';
    };

    const nextStepHandler = () => setFormStep(formStep + 1);

    const editStepHandler = (step: number) => {
        setFormStep(step);
        setIsEditingSpecificStep(true);
    };

    const handlePetTypeStep = (petTypes: Array<PetType>, marketplaces: Service['marketplaces']) => {
        setBoardingService({
            ...boardingService,
            petTypes,
            marketplaces
        });
        nextStepHandler();
    };

    const handleDetailsStep = (details: {
        name: string;
        description: string;
        checkInTime: TimeRange;
        checkOutTime: TimeRange;
        timeMode: '24hours' | 'setTimes';
        color?: string;
        onlineBooking: boolean;
        forms?: import('model/Forms').AssociatedForm[];
        selectedRooms: Array<string>;
        marketplaces: Service['marketplaces'];
        minNights?: number;
    }) => {
        const existingBoardingData = boardingService.boardingData;
        const billingCalculation = resolveBillingCalculation(
            existingBoardingData?.billingCalculation,
            existingBoardingData?.dropoff24h
        );
        const boardingData: BoardingMetadata = {
            billingCalculation,
            checkoutCutoffTime: existingBoardingData?.checkoutCutoffTime,
            checkoutGracePeriod: existingBoardingData?.checkoutGracePeriod,
            dropoff24h: dropoff24hFromBillingCalculation(billingCalculation),
            roomsEnabled: true,
            selectedRooms: details.selectedRooms || [],
            minNights: details.minNights || existingBoardingData?.minNights || 1,
            latePickupFee: existingBoardingData?.latePickupFee || {
                enabled: false,
                type: LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                gracePeriod: 30,
                fees: {}
            }
        };

        setBoardingService({
            ...boardingService,
            name: details.name,
            description: details.description,
            marketplaces: details.marketplaces,
            color: details.color || '',
            olbEnabled: details.onlineBooking,
            forms: details.forms || [],
            serviceItems: [],
            checkInTime:
                details.timeMode === 'setTimes'
                    ? {
                          start: timeStringToInteger(details.checkInTime.from),
                          end: timeStringToInteger(details.checkInTime.to)
                      }
                    : boardingService.checkInTime,
            checkOutTime:
                details.timeMode === 'setTimes'
                    ? {
                          start: timeStringToInteger(details.checkOutTime.from),
                          end: timeStringToInteger(details.checkOutTime.to)
                      }
                    : boardingService.checkOutTime,
            boardingData,
            duration: {
                metadata: JSON.stringify(buildBoardingRawMetadata(boardingData))
            } as MetadataDuration
        });
        nextStepHandler();
    };

    const handlePricingStep = async (
        pricing: ServicePricing,
        boardingData: {
            billingCalculation: BillingCalculation;
            checkoutCutoffTime?: number;
            checkoutGracePeriod?: number;
            dropoff24h: boolean;
            minNights: number;
            latePickupFee: {
                enabled: boolean;
                type?: LateCheckoutFeeType;
                gracePeriod: number;
                fees: Record<string, number>;
            };
        },
        marketplaces: Service['marketplaces']
    ) => {
        try {
            setIsLoading(true);

            const currentRoomsEnabled = boardingService.boardingData?.roomsEnabled ?? true;
            const currentSelectedRooms = boardingService.boardingData?.selectedRooms || [];
            const billingCalculation = resolveBillingCalculation(
                boardingData.billingCalculation,
                boardingData.dropoff24h
            );

            const parsedBoardingData: BoardingMetadata = {
                billingCalculation,
                checkoutCutoffTime: boardingData.checkoutCutoffTime,
                checkoutGracePeriod: boardingData.checkoutGracePeriod,
                dropoff24h: dropoff24hFromBillingCalculation(billingCalculation),
                roomsEnabled: currentRoomsEnabled,
                selectedRooms: currentSelectedRooms,
                minNights: boardingData.minNights,
                latePickupFee: {
                    enabled: boardingData.latePickupFee.enabled,
                    type: boardingData.latePickupFee.type || LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                    gracePeriod: boardingData.latePickupFee.gracePeriod,
                    fees: boardingData.latePickupFee.fees
                }
            };

            const finalBoardingService: Service = {
                ...boardingService,
                marketplaces,
                serviceItems: [],
                pricing,
                checkInTime: boardingService.checkInTime,
                checkOutTime: boardingService.checkOutTime,
                boardingData: parsedBoardingData,
                duration: {
                    metadata: JSON.stringify(buildBoardingRawMetadata(parsedBoardingData))
                } as MetadataDuration
            };

            await dispatch(saveBulkServicesThunk(finalBoardingService));
        } catch (error) {
            // Show error notification
        } finally {
            setIsLoading(false);
        }
    };

    const getActiveStep = () => {
        if (props.mode === 'edit' && !isEditingSpecificStep) {
            return 3; // Show all steps completed only in initial state
        }
        return formStep;
    };

    // Memoize formData for BoardingDetailsStep
    const boardingDetailsFormData = useMemo(() => {
        const hasConfiguredTimes = !!(
            boardingService.checkInTime?.start &&
            boardingService.checkInTime?.end &&
            boardingService.checkOutTime?.start &&
            boardingService.checkOutTime?.end
        );

        return {
            name: boardingService.name,
            description: boardingService.description,
            checkInTime: hasConfiguredTimes
                ? {
                      from: integerTimeToString(boardingService.checkInTime!.start, '12h'),
                      to: integerTimeToString(boardingService.checkInTime!.end, '12h')
                  }
                : { from: '2:00 PM', to: '5:00 PM' },
            checkOutTime: hasConfiguredTimes
                ? {
                      from: integerTimeToString(boardingService.checkOutTime!.start, '12h'),
                      to: integerTimeToString(boardingService.checkOutTime!.end, '12h')
                  }
                : { from: '9:00 AM', to: '12:00 PM' },
            timeMode: (hasConfiguredTimes ? 'setTimes' : '24hours') as '24hours' | 'setTimes',
            color: boardingService.color || undefined,
            onlineBooking: boardingService.olbEnabled,
            forms: boardingService.forms || [],
            selectedRooms: boardingService.boardingData?.selectedRooms || [],
            minNights: boardingService.boardingData?.minNights || 1
        };
    }, [
        boardingService.name,
        boardingService.description,
        boardingService.checkInTime,
        boardingService.checkOutTime,
        boardingService.color,
        boardingService.olbEnabled,
        boardingService.forms,
        boardingService.boardingData?.selectedRooms,
        boardingService.boardingData?.minNights
    ]);

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={getTitle()}
                onClose={props.onClose}
            >
                <SuccessScreen
                    title={getSuccessTitle()}
                    message={getSuccessMessage()}
                    buttonLabel={getSuccessButtonLabel()}
                    onClick={props.onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title={getTitle()}
            onClose={() => setShowExitPrompt(true)}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={() => props.onClose?.()}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            {isLoadingOrganizationServices ? (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            ) : (
                <div className={styles.container}>
                    <MultiStepForm
                        steps={[
                            {
                                title: 'Pet Type and Locations',
                                description: 'Select pet categories for this service',
                                component: (
                                    <BoardingPetTypeStep
                                        selectedPetTypes={boardingService.petTypes}
                                        marketplaces={boardingService.marketplaces}
                                        serviceCategory="boarding"
                                        serviceId={boardingService.id}
                                        onNext={handlePetTypeStep}
                                    />
                                )
                            },
                            {
                                title: 'Boarding Service Detail',
                                component: (
                                    <BoardingDetailsStep
                                        formData={boardingDetailsFormData}
                                        petTypes={boardingService.petTypes || []}
                                        marketplaces={boardingService.marketplaces}
                                        availableRooms={availableRoomsFiltered}
                                        roomsLoading={roomsLoading}
                                        serviceId={boardingService.id}
                                        onNext={handleDetailsStep}
                                    />
                                )
                            },
                            {
                                title: 'Pricing',
                                description: 'Choose a pricing option for this boarding service',
                                component: (
                                    <BoardingPricingStep
                                        selectedPetTypes={boardingService.petTypes}
                                        marketplaces={boardingService.marketplaces}
                                        existingService={boardingService}
                                        onNext={handlePricingStep}
                                    />
                                )
                            }
                        ]}
                        activeStep={getActiveStep()}
                        onEdit={editStepHandler}
                        flexibleNavigation={props.mode === 'edit'} //Enable flexible navigation only in edit mode
                    />
                </div>
            )}
        </OverFullWindow>
    );
};

export default BoardingForm;
